/**
 * v3.0 Context-Aware Group Navigation Bar.
 *
 * Renders 3 group pills (Procurement / Contracts / Claims) and the modules
 * of the active group beneath them. The active group is derived from the
 * current route. Clicking a group pill navigates to the first module of
 * that group.
 */
import { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ShoppingBag, FileText, Scale } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import {
  PROCUREMENT_MODULES, CONTRACTS_MODULES, CLAIMS_LD_MODULES
} from '@/config/navigation';

const GROUPS = [
  { key: 'procurement', label: 'Procurement', icon: ShoppingBag,
    color: '#10B981', modules: PROCUREMENT_MODULES },
  { key: 'contracts',   label: 'Contracts',   icon: FileText,
    color: '#3B82F6', modules: CONTRACTS_MODULES },
  { key: 'claims',      label: 'Claims & LD', icon: Scale,
    color: '#F59E0B', modules: CLAIMS_LD_MODULES },
];

// iter-238 — Apply the user's `restrictions.allowed_module_keys`
// whitelist, if any, so restricted demo accounts only see the modules
// they're allowed to visit.
function applyModuleRestrictions(user) {
  const allowed = user?.restrictions?.allowed_module_keys;
  if (!Array.isArray(allowed) || allowed.length === 0) return GROUPS;
  return GROUPS
    .map(g => ({ ...g, modules: g.modules.filter(m => allowed.includes(m.key)) }))
    .filter(g => g.modules.length > 0);
}

function pathInGroup(pathname, modules) {
  const stripQ = (p) => (p || '').split('?')[0];
  return modules.some(m =>
    m.children?.some(c => {
      const cp = stripQ(c.path);
      return pathname === cp || pathname.startsWith(cp + '/');
    }));
}

export default function GroupNavBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // iter-238 — filter the groups+modules by the user's allowed keys.
  const groups = useMemo(() => applyModuleRestrictions(user), [user]);

  const activeGroupKey = useMemo(() => {
    for (const g of groups) {
      if (pathInGroup(location.pathname, g.modules)) return g.key;
    }
    return null;
  }, [location.pathname, groups]);

  // Default to first allowed group when off-group.
  const activeGroup = groups.find(g => g.key === activeGroupKey) || groups[0];
  const isExplicitlyActive = !!activeGroupKey;
  const activeModuleKey = useMemo(() => {
    if (!activeGroup) return null;
    const stripQ = (p) => (p || '').split('?')[0];
    const mod = activeGroup.modules.find(m =>
      m.children?.some(c => {
        const cp = stripQ(c.path);
        return location.pathname === cp || location.pathname.startsWith(cp + '/');
      }));
    return mod?.key;
  }, [activeGroup, location.pathname]);

  if (!activeGroup) return null;
  // Hide on admin top-levels (admin pages have their own contextual nav)
  if (location.pathname.startsWith('/app/admin')) return null;

  const goToGroup = (g) => {
    const firstMod = g.modules[0];
    const firstChild = firstMod?.children?.[0];
    if (firstChild?.path) navigate(firstChild.path);
  };

  return (
    <div className="border-b border-gray-100 bg-white shrink-0" data-testid="group-nav-bar">
      {/* Group pills row */}
      <div className="flex items-center px-4 py-1.5 gap-1.5">
        <span className="text-[9px] font-semibold uppercase tracking-wider text-gray-400 mr-2">
          Group
        </span>
        {groups.map(g => {
          const Icon = g.icon;
          const isActive = isExplicitlyActive && g.key === activeGroupKey;
          return (
            <button
              key={g.key}
              onClick={() => goToGroup(g)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition-all duration-200 ${
                isActive
                  ? 'text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
              style={isActive ? { backgroundColor: g.color } : {}}
              data-testid={`group-pill-${g.key}`}
            >
              <Icon className="w-3 h-3" />
              {g.label}
              <span className={`text-[9px] ${isActive ? 'text-white/80' : 'text-gray-400'}`}>
                {g.modules.length}
              </span>
            </button>
          );
        })}
        <div className="w-px h-4 bg-gray-200 mx-1" />
        <span className="text-[9px] uppercase tracking-wider text-gray-400">
          Modules ({activeGroup.modules.length})
        </span>
      </div>

      {/* Modules row */}
      <div className="flex items-center px-4 pb-1.5 gap-1 flex-wrap">
        {activeGroup.modules.map(mod => {
          const ModIcon = mod.icon;
          const isActive = mod.key === activeModuleKey;
          const firstChild = mod.children?.[0];
          return (
            <button
              key={mod.key}
              onClick={() => firstChild?.path && navigate(firstChild.path)}
              className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] whitespace-nowrap transition-all duration-200 ${
                isActive
                  ? 'bg-[#1E4E8C]/10 text-[#1E4E8C] font-semibold ring-1 ring-[#1E4E8C]/20'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              data-testid={`module-pill-${mod.key}`}
              title={mod.subtitle}
            >
              <div
                className="w-3.5 h-3.5 rounded flex items-center justify-center"
                style={{ background: `linear-gradient(135deg, ${mod.color}, ${mod.colorEnd})` }}
              >
                <ModIcon className="w-2 h-2 text-white" strokeWidth={2.5} />
              </div>
              {mod.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
